import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatMXN, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet, Pencil, Check, X, Settings, Download } from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const PIE_COLORS = ["#8B5A33", "#4A7C59", "#BC4749", "#D4A373", "#6D4525", "#2f3d31", "#8a3537", "#3a6347", "#c99a6b", "#7A756D"];

const CATALOGO_LABELS = {
  concepto_egreso: "Conceptos de Egreso",
  concepto_ingreso: "Conceptos de Ingreso",
  egreso_tipo: "Tipo de Egreso",
  forma_pago: "Formas de pago",
};

// Combobox nativo (input + datalist) — permite escribir libremente o elegir de la lista.
const Combobox = ({ value, onChange, options, placeholder, id, testid, className }) => {
  const listId = `dl-${id}`;
  const uniq = Array.from(new Set((options || []).filter(Boolean)));
  return (
    <>
      <Input
        list={listId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        data-testid={testid}
        autoComplete="off"
      />
      <datalist id={listId}>
        {uniq.map((o) => <option key={o} value={o} />)}
      </datalist>
    </>
  );
};

export default function Gastos() {
  const now = new Date();
  const [anio, setAnio] = useState(now.getFullYear());
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [items, setItems] = useState([]);
  const [anioItems, setAnioItems] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [resumen, setResumen] = useState({ total_ingresos: 0, total_egresos: 0, balance: 0, por_mes: {} });
  const [catalogos, setCatalogos] = useState({ concepto_egreso: [], concepto_ingreso: [], egreso_tipo: [], forma_pago: [] });
  const [editRow, setEditRow] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [filtroTipoEgreso, setFiltroTipoEgreso] = useState("__all__");

  const empty = () => ({
    fecha: `${anio}-${String(mes).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`,
    tipo: "Egreso",
    concepto: "",
    descripcion: "",
    proveedor_cliente: "",
    proyecto: "",
    categoria: "",
    forma_pago: "",
    monto: 0,
    factura: "",
    egreso_tipo: "",
    cotizacion_id: null,
    cotizacion_folio: null,
  });
  const [nuevo, setNuevo] = useState(empty());
  // Cuando el auto-jump viene de handleFechaChange, no queremos resetear el formulario
  const skipResetRef = useRef(false);

  const loadCatalogos = async () => {
    const { data } = await api.get("/gastos-catalogos");
    setCatalogos(data);
  };

  const load = async () => {
    const [g, ga, r, c] = await Promise.all([
      api.get(`/gastos?anio=${anio}&mes=${mes}`),
      api.get(`/gastos?anio=${anio}`),
      api.get(`/gastos-resumen?anio=${anio}`),
      api.get("/cotizaciones"),
    ]);
    setItems(g.data);
    setAnioItems(ga.data);
    setResumen(r.data);
    setCotizaciones(c.data);
  };

  useEffect(() => { loadCatalogos(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anio, mes]);
  useEffect(() => {
    if (skipResetRef.current) { skipResetRef.current = false; return; }
    setNuevo(empty());
    /* eslint-disable-next-line */
  }, [anio, mes]);

  // Auto-jump mes/anio si el usuario captura una fecha diferente a la pestaña activa
  const handleFechaChange = (fecha) => {
    setNuevo((n) => ({ ...n, fecha }));
    if (!fecha || fecha.length < 7) return;
    const [y, m] = fecha.split("-");
    const yy = Number(y), mm = Number(m);
    if (!yy || !mm) return;
    if (yy !== anio || mm !== mes) skipResetRef.current = true;
    if (yy !== anio) setAnio(yy);
    if (mm !== mes) setMes(mm);
  };

  // Opciones combinadas: catálogo persistente + valores usados en anio actual
  const opciones = useMemo(() => {
    const merge = (baseArr, historicos) => {
      const s = new Set(baseArr.map((x) => x.valor));
      historicos.forEach((v) => v && s.add(v));
      return Array.from(s).sort((a, b) => a.localeCompare(b));
    };
    const histConceptoEgr = anioItems.filter((g) => g.tipo === "Egreso").map((g) => g.concepto);
    const histConceptoIng = anioItems.filter((g) => g.tipo === "Ingreso").map((g) => g.concepto);
    const histEgresoTipo = anioItems.map((g) => g.egreso_tipo);
    const histFormaPago = anioItems.map((g) => g.forma_pago);
    return {
      conceptoEgreso: merge(catalogos.concepto_egreso, histConceptoEgr),
      conceptoIngreso: merge(catalogos.concepto_ingreso, histConceptoIng),
      egresoTipo: merge(catalogos.egreso_tipo, histEgresoTipo),
      formaPago: merge(catalogos.forma_pago, histFormaPago),
    };
  }, [catalogos, anioItems]);

  const agregar = async () => {
    if (!nuevo.fecha || !nuevo.concepto) { toast.error("Fecha y concepto obligatorios"); return; }
    try {
      await api.post("/gastos", { ...nuevo, monto: Number(nuevo.monto) || 0 });
      toast.success(`${nuevo.tipo} registrado`);
      setNuevo(empty());
      load();
    } catch { toast.error("Error al guardar"); }
  };

  const startEdit = (g) => {
    setEditRow(g.id);
    setEditDraft({
      fecha: g.fecha,
      tipo: g.tipo,
      concepto: g.concepto || "",
      egreso_tipo: g.egreso_tipo || "",
      cotizacion_id: g.cotizacion_id || null,
      cotizacion_folio: g.cotizacion_folio || null,
      proveedor_cliente: g.proveedor_cliente || "",
      forma_pago: g.forma_pago || "",
      factura: g.factura || "",
      monto: g.monto,
    });
  };
  const cancelEdit = () => { setEditRow(null); setEditDraft({}); };
  const saveEdit = async (id) => {
    try {
      const payload = { ...editDraft, monto: Number(editDraft.monto) || 0 };
      if (payload.tipo !== "Ingreso") { payload.cotizacion_id = null; payload.cotizacion_folio = null; }
      await api.put(`/gastos/${id}`, payload);
      toast.success("Actualizado");
      cancelEdit();
      load();
    } catch { toast.error("No se pudo actualizar"); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    await api.delete(`/gastos/${id}`);
    toast.success("Eliminado");
    load();
  };

  const exportar = async () => {
    try {
      const params = { anio, mes };
      if (filtroTipoEgreso !== "__all__") params.egreso_tipo = filtroTipoEgreso;
      const { data } = await api.get("/gastos/export", { params, responseType: "blob" });
      const href = URL.createObjectURL(data);
      const dl = document.createElement("a");
      dl.href = href;
      dl.download = `gastos_${anio}_${String(mes).padStart(2, "0")}.xlsx`;
      document.body.appendChild(dl); dl.click(); dl.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar"); }
  };

  const totalesMes = useMemo(() => {
    const ing = items.filter((x) => x.tipo === "Ingreso").reduce((s, x) => s + x.monto, 0);
    const eg = items.filter((x) => x.tipo === "Egreso").reduce((s, x) => s + x.monto, 0);
    return { ingresos: ing, egresos: eg, balance: ing - eg };
  }, [items]);

  // Filtro por tipo de egreso (afecta la tabla). Los ingresos siempre se muestran.
  const itemsFiltrados = useMemo(() => {
    if (filtroTipoEgreso === "__all__") return items;
    return items.filter((g) => g.tipo === "Ingreso" || (g.egreso_tipo || "Sin definir") === filtroTipoEgreso);
  }, [items, filtroTipoEgreso]);

  const egresosPie = useMemo(() => {
    const egresos = items.filter((x) => x.tipo === "Egreso");
    const total = egresos.reduce((s, x) => s + (Number(x.monto) || 0), 0);
    const groupBy = (key, fallback = "Sin definir") => {
      const map = {};
      egresos.forEach((x) => {
        const k = (x[key] || "").trim() || fallback;
        map[k] = (map[k] || 0) + (Number(x.monto) || 0);
      });
      return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    };
    return { total, porTipo: groupBy("egreso_tipo"), porConcepto: groupBy("concepto") };
  }, [items]);

  const tiposEgresoUsados = useMemo(() => {
    const s = new Set();
    items.filter((x) => x.tipo === "Egreso").forEach((x) => s.add(x.egreso_tipo || "Sin definir"));
    return Array.from(s).sort();
  }, [items]);

  const nuevoConceptos = nuevo.tipo === "Ingreso" ? opciones.conceptoIngreso : opciones.conceptoEgreso;

  return (
    <div className="space-y-6" data-testid="gastos-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="uppercase-label">Control contable</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 flex items-center gap-3">
            <Wallet className="w-9 h-9 text-[#8B5A33]" strokeWidth={1.5} /> Control de gastos
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Registra ingresos y egresos del negocio. Cada mes tiene su propia hoja.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportar} className="border-[#8B5A33]/40 text-[#8B5A33] hover:bg-[#8B5A33]/10" data-testid="btn-export-xlsx">
            <Download className="w-4 h-4 mr-2" /> Exportar Excel
          </Button>
          <CatalogoManager catalogos={catalogos} onChange={loadCatalogos} />
          <Select value={String(anio)} onValueChange={(v) => setAnio(Number(v))}>
            <SelectTrigger className="w-32" data-testid="gastos-anio"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Anual summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-[#4A7C59]/30 bg-[#4A7C59]/10 rounded-lg p-4">
          <div className="flex items-center gap-2 uppercase-label"><TrendingUp className="w-4 h-4 text-[#4A7C59]" /> Ingresos {anio}</div>
          <div className="font-display text-3xl tabular mt-1 text-[#3a6347]" data-testid="tot-ingresos">{formatMXN(resumen.total_ingresos)}</div>
        </div>
        <div className="border border-[#BC4749]/30 bg-[#BC4749]/10 rounded-lg p-4">
          <div className="flex items-center gap-2 uppercase-label"><TrendingDown className="w-4 h-4 text-[#BC4749]" /> Egresos {anio}</div>
          <div className="font-display text-3xl tabular mt-1 text-[#8a3537]" data-testid="tot-egresos">{formatMXN(resumen.total_egresos)}</div>
        </div>
        <div className={`border rounded-lg p-4 ${resumen.balance >= 0 ? "border-[#8B5A33]/30 bg-[#8B5A33]/10" : "border-[#BC4749]/40 bg-[#BC4749]/10"}`}>
          <div className="flex items-center gap-2 uppercase-label"><Wallet className="w-4 h-4 text-[#8B5A33]" /> Balance {anio}</div>
          <div className="font-display text-3xl tabular mt-1" data-testid="tot-balance">{formatMXN(resumen.balance)}</div>
        </div>
      </div>

      {/* Month tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border">
        {MESES.map((n, i) => {
          const m = i + 1;
          const dataMes = resumen.por_mes?.[m] || { ingresos: 0, egresos: 0 };
          const activo = m === mes;
          return (
            <button
              key={m}
              onClick={() => setMes(m)}
              className={`px-3 py-2 text-sm border-b-2 transition-colors ${activo ? "border-[#8B5A33] text-[#8B5A33] font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`gastos-mes-${m}`}
            >
              {n}
              {(dataMes.ingresos > 0 || dataMes.egresos > 0) && (
                <span className="ml-1.5 text-[10px] tabular text-muted-foreground">·</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Month totals */}
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="border border-border bg-white rounded p-3">
          <div className="uppercase-label">Ingresos {MESES[mes-1]}</div>
          <div className="font-display text-xl tabular mt-1 text-[#3a6347]">{formatMXN(totalesMes.ingresos)}</div>
        </div>
        <div className="border border-border bg-white rounded p-3">
          <div className="uppercase-label">Egresos {MESES[mes-1]}</div>
          <div className="font-display text-xl tabular mt-1 text-[#8a3537]">{formatMXN(totalesMes.egresos)}</div>
        </div>
        <div className="border border-border bg-white rounded p-3">
          <div className="uppercase-label">Balance mes</div>
          <div className={`font-display text-xl tabular mt-1 ${totalesMes.balance >= 0 ? "text-[#3a6347]" : "text-[#8a3537]"}`}>{formatMXN(totalesMes.balance)}</div>
        </div>
      </div>

      {/* Nuevo movimiento */}
      <div className="bg-white border border-border rounded-lg p-4">
        <div className="uppercase-label mb-2">Registrar movimiento</div>
        <div className="grid grid-cols-2 md:grid-cols-9 gap-2">
          <Input type="date" value={nuevo.fecha} onChange={(e) => handleFechaChange(e.target.value)} data-testid="new-fecha" />
          <Select value={nuevo.tipo} onValueChange={(v) => setNuevo({ ...nuevo, tipo: v, cotizacion_id: v === "Ingreso" ? nuevo.cotizacion_id : null, egreso_tipo: v === "Ingreso" ? "" : nuevo.egreso_tipo })}>
            <SelectTrigger data-testid="new-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Ingreso">Ingreso</SelectItem>
              <SelectItem value="Egreso">Egreso</SelectItem>
            </SelectContent>
          </Select>
          <Combobox id="new-concepto" testid="new-concepto" value={nuevo.concepto} onChange={(v) => setNuevo({ ...nuevo, concepto: v })} options={nuevoConceptos} placeholder="Concepto" />
          {nuevo.tipo === "Egreso" ? (
            <Combobox id="new-egreso-tipo" testid="new-egreso-tipo" value={nuevo.egreso_tipo} onChange={(v) => setNuevo({ ...nuevo, egreso_tipo: v })} options={opciones.egresoTipo} placeholder="Tipo (Costos, Gastos…)" />
          ) : (
            <Select
              value={nuevo.cotizacion_id || "__none__"}
              onValueChange={(v) => {
                if (v === "__none__") { setNuevo({ ...nuevo, cotizacion_id: null, cotizacion_folio: null }); return; }
                const c = cotizaciones.find((x) => x.id === v);
                setNuevo({ ...nuevo, cotizacion_id: v, cotizacion_folio: c?.folio || null, proveedor_cliente: c?.cliente_nombre || nuevo.proveedor_cliente, proyecto: c?.proyecto || nuevo.proyecto });
              }}
            >
              <SelectTrigger data-testid="new-cotizacion"><SelectValue placeholder="Ligar cotización" /></SelectTrigger>
              <SelectContent className="max-h-[280px]">
                <SelectItem value="__none__">— Sin cotización —</SelectItem>
                {cotizaciones.filter((c) => c?.id).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.folio} · {c.cliente_nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Input placeholder="Proveedor / cliente" value={nuevo.proveedor_cliente} onChange={(e) => setNuevo({ ...nuevo, proveedor_cliente: e.target.value })} data-testid="new-proveedor" />
          <Combobox id="new-forma-pago" testid="new-forma-pago" value={nuevo.forma_pago} onChange={(v) => setNuevo({ ...nuevo, forma_pago: v })} options={opciones.formaPago} placeholder="Forma de pago" />
          <Input placeholder="No. factura" value={nuevo.factura} onChange={(e) => setNuevo({ ...nuevo, factura: e.target.value })} data-testid="new-factura" />
          <Input type="number" step="0.01" placeholder="Monto" value={nuevo.monto} onChange={(e) => setNuevo({ ...nuevo, monto: e.target.value })} className="text-right tabular" data-testid="new-monto" />
          <Button onClick={agregar} className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" data-testid="new-add">
            <Plus className="w-4 h-4 mr-1" /> Agregar
          </Button>
        </div>
      </div>

      {/* Filtro por tipo de egreso */}
      <div className="flex flex-wrap items-center gap-2" data-testid="filtro-tipo-egreso">
        <span className="uppercase-label mr-1">Filtrar egresos:</span>
        <FilterChip active={filtroTipoEgreso === "__all__"} onClick={() => setFiltroTipoEgreso("__all__")} testid="filtro-all">Todos</FilterChip>
        {opciones.egresoTipo.map((t) => (
          <FilterChip key={t} active={filtroTipoEgreso === t} onClick={() => setFiltroTipoEgreso(t)} testid={`filtro-${t}`}>{t}</FilterChip>
        ))}
        {tiposEgresoUsados.filter((t) => t === "Sin definir").map((t) => (
          <FilterChip key={t} active={filtroTipoEgreso === t} onClick={() => setFiltroTipoEgreso(t)} testid="filtro-sindefinir" muted>Sin definir</FilterChip>
        ))}
      </div>

      {/* Lista del mes */}
      <div className="border border-border bg-white rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F2EC]/70 text-left">
            <tr>
              <th className="px-3 py-2 uppercase-label w-28">Fecha</th>
              <th className="px-3 py-2 uppercase-label w-24">Tipo</th>
              <th className="px-3 py-2 uppercase-label">Concepto</th>
              <th className="px-3 py-2 uppercase-label w-32">Tipo egreso / Cotización</th>
              <th className="px-3 py-2 uppercase-label">Proveedor / cliente</th>
              <th className="px-3 py-2 uppercase-label w-28">Forma pago</th>
              <th className="px-3 py-2 uppercase-label w-28">Factura</th>
              <th className="px-3 py-2 uppercase-label w-32 text-right">Monto</th>
              <th className="px-3 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {itemsFiltrados.length === 0 && (
              <tr><td colSpan={9} className="px-6 py-10 text-center text-muted-foreground text-sm">Sin movimientos {filtroTipoEgreso !== "__all__" ? `de tipo "${filtroTipoEgreso}"` : ""} en {MESES[mes-1]} {anio}.</td></tr>
            )}
            {itemsFiltrados.map((g) => {
              const isEditing = editRow === g.id;
              if (!isEditing) {
                return (
                  <tr key={g.id} className="border-t border-border hover:bg-[#FAFAF7]" data-testid={`gasto-row-${g.id}`}>
                    <td className="px-3 py-2 text-xs">{formatDate(g.fecha)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${g.tipo === "Ingreso" ? "bg-[#4A7C59]/15 text-[#3a6347] border-[#4A7C59]/30" : "bg-[#BC4749]/15 text-[#8a3537] border-[#BC4749]/30"}`}>{g.tipo}</span>
                    </td>
                    <td className="px-3 py-2">{g.concepto || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2 text-xs">
                      {g.tipo === "Ingreso"
                        ? <span className="tabular text-[#8B5A33]">{g.cotizacion_folio || "—"}</span>
                        : (g.egreso_tipo
                            ? <span className="inline-block text-[10px] px-2 py-0.5 rounded bg-[#8B5A33]/10 text-[#6D4525] border border-[#8B5A33]/30">{g.egreso_tipo}</span>
                            : <span className="text-muted-foreground">—</span>)}
                    </td>
                    <td className="px-3 py-2">{g.proveedor_cliente || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2">{g.forma_pago || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2 text-xs tabular">{g.factura || <span className="text-muted-foreground">—</span>}</td>
                    <td className={`px-3 py-2 text-right tabular font-medium ${g.tipo === "Ingreso" ? "text-[#3a6347]" : "text-[#8a3537]"}`}>{formatMXN(g.monto)}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => startEdit(g)} title="Editar" data-testid={`edit-${g.id}`}><Pencil className="w-4 h-4 text-[#8B5A33]" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => eliminar(g.id)} title="Eliminar" data-testid={`del-${g.id}`}><Trash2 className="w-4 h-4 text-[#BC4749]" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              }
              const editConceptos = editDraft.tipo === "Ingreso" ? opciones.conceptoIngreso : opciones.conceptoEgreso;
              return (
                <tr key={g.id} className="border-t border-border bg-[#F4F2EC]/60" data-testid={`gasto-row-edit-${g.id}`}>
                  <td className="px-2 py-1.5"><Input type="date" value={editDraft.fecha} onChange={(e) => setEditDraft({ ...editDraft, fecha: e.target.value })} className="h-8" data-testid={`edit-fecha-${g.id}`} /></td>
                  <td className="px-2 py-1.5">
                    <Select value={editDraft.tipo} onValueChange={(v) => setEditDraft({ ...editDraft, tipo: v, egreso_tipo: v === "Ingreso" ? "" : editDraft.egreso_tipo })}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ingreso">Ingreso</SelectItem>
                        <SelectItem value="Egreso">Egreso</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5"><Combobox id={`edit-concepto-${g.id}`} testid={`edit-concepto-${g.id}`} value={editDraft.concepto} onChange={(v) => setEditDraft({ ...editDraft, concepto: v })} options={editConceptos} placeholder="Concepto" className="h-8" /></td>
                  <td className="px-2 py-1.5">
                    {editDraft.tipo === "Egreso" ? (
                      <Combobox id={`edit-egreso-tipo-${g.id}`} testid={`edit-egreso-tipo-${g.id}`} value={editDraft.egreso_tipo} onChange={(v) => setEditDraft({ ...editDraft, egreso_tipo: v })} options={opciones.egresoTipo} placeholder="Tipo egreso" className="h-8" />
                    ) : (
                      <Select
                        value={editDraft.cotizacion_id || "__none__"}
                        onValueChange={(v) => {
                          if (v === "__none__") { setEditDraft({ ...editDraft, cotizacion_id: null, cotizacion_folio: null }); return; }
                          const c = cotizaciones.find((x) => x.id === v);
                          setEditDraft({ ...editDraft, cotizacion_id: v, cotizacion_folio: c?.folio || null });
                        }}
                      >
                        <SelectTrigger className="h-8"><SelectValue placeholder="Cotización" /></SelectTrigger>
                        <SelectContent className="max-h-[280px]">
                          <SelectItem value="__none__">— Sin cotización —</SelectItem>
                          {cotizaciones.filter((c) => c?.id).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.folio} · {c.cliente_nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-2 py-1.5"><Input value={editDraft.proveedor_cliente} onChange={(e) => setEditDraft({ ...editDraft, proveedor_cliente: e.target.value })} className="h-8" data-testid={`edit-proveedor-${g.id}`} /></td>
                  <td className="px-2 py-1.5"><Combobox id={`edit-forma-${g.id}`} testid={`edit-forma-${g.id}`} value={editDraft.forma_pago} onChange={(v) => setEditDraft({ ...editDraft, forma_pago: v })} options={opciones.formaPago} placeholder="Forma pago" className="h-8" /></td>
                  <td className="px-2 py-1.5"><Input value={editDraft.factura} onChange={(e) => setEditDraft({ ...editDraft, factura: e.target.value })} className="h-8 tabular text-xs" data-testid={`edit-factura-${g.id}`} /></td>
                  <td className="px-2 py-1.5"><Input type="number" step="0.01" value={editDraft.monto} onChange={(e) => setEditDraft({ ...editDraft, monto: e.target.value })} className="h-8 text-right tabular" data-testid={`edit-monto-${g.id}`} /></td>
                  <td className="px-2 py-1.5">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => saveEdit(g.id)} title="Guardar" data-testid={`save-${g.id}`}><Check className="w-4 h-4 text-[#3a6347]" /></Button>
                      <Button size="icon" variant="ghost" onClick={cancelEdit} title="Cancelar" data-testid={`cancel-${g.id}`}><X className="w-4 h-4 text-[#8a3537]" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Gráficas de egresos */}
      <section data-testid="egresos-charts" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Egresos por tipo" subtitle={`Distribución en ${MESES[mes-1]} ${anio}`} data={egresosPie.porTipo} total={egresosPie.total} testid="chart-egreso-tipo" />
        <ChartCard title="Egresos por concepto" subtitle={`Distribución en ${MESES[mes-1]} ${anio}`} data={egresosPie.porConcepto} total={egresosPie.total} testid="chart-egreso-concepto" />
      </section>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid, muted }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
        active
          ? "bg-[#8B5A33] text-white border-[#8B5A33]"
          : muted
          ? "bg-white text-muted-foreground border-dashed border-border hover:border-[#8B5A33]/60"
          : "bg-white text-foreground border-border hover:border-[#8B5A33]/60"
      }`}
    >
      {children}
    </button>
  );
}

function ChartCard({ title, subtitle, data, total, testid }) {
  const hasData = data && data.length > 0 && total > 0;
  return (
    <div className="border border-border bg-white rounded-lg p-5" data-testid={testid}>
      <div className="uppercase-label mb-1">{subtitle}</div>
      <h3 className="font-display text-xl mb-3">{title}</h3>
      {!hasData ? (
        <div className="py-14 text-center text-muted-foreground text-sm">Sin egresos registrados este mes.</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={95} paddingAngle={2} label={(e) => `${((e.percent || 0) * 100).toFixed(1)}%`} labelLine={false}>
                {data.map((entry, i) => <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [formatMXN(v), n]} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="text-xs border-t border-border pt-2 mt-1 space-y-0.5 max-h-32 overflow-auto">
            {data.map((d, i) => (
              <div key={d.name} className="flex justify-between">
                <span className="flex items-center gap-2"><span className="inline-block w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{d.name}</span>
                <span className="tabular text-muted-foreground">{formatMXN(d.value)} · {((d.value / total) * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Dialogo para agregar / quitar valores de los catálogos
function CatalogoManager({ catalogos, onChange }) {
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState("concepto_egreso");
  const [nuevo, setNuevo] = useState("");

  const agregar = async () => {
    const valor = nuevo.trim();
    if (!valor) return;
    try {
      await api.post("/gastos-catalogos", { tipo, valor });
      setNuevo("");
      onChange();
      toast.success("Opción agregada");
    } catch { toast.error("No se pudo agregar"); }
  };

  const quitar = async (id) => {
    try {
      await api.delete(`/gastos-catalogos/${id}`);
      onChange();
      toast.success("Opción eliminada");
    } catch { toast.error("No se pudo eliminar"); }
  };

  const items = catalogos[tipo] || [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-[#8B5A33]/40 text-[#8B5A33] hover:bg-[#8B5A33]/10" data-testid="btn-catalogos">
          <Settings className="w-4 h-4 mr-2" /> Catálogos
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="dialog-catalogos">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Gestionar opciones</DialogTitle>
          <DialogDescription>Agrega o quita valores usados en los desplegables de Concepto, Tipo de egreso y Forma de pago.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="uppercase-label block mb-1">Tipo de lista</label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="catalogo-select-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATALOGO_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Input placeholder="Nueva opción" value={nuevo} onChange={(e) => setNuevo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && agregar()} data-testid="catalogo-input-nuevo" />
            <Button onClick={agregar} className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" data-testid="catalogo-btn-agregar">
              <Plus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>
          <div className="border border-border rounded-md max-h-72 overflow-auto">
            {items.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Sin opciones. Agrega la primera arriba.</div>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((it) => (
                  <li key={it.id} className="flex items-center justify-between px-3 py-2 hover:bg-[#FAFAF7]" data-testid={`catalogo-item-${it.id}`}>
                    <span className="text-sm">{it.valor}</span>
                    <Button size="icon" variant="ghost" onClick={() => quitar(it.id)} title="Quitar" data-testid={`catalogo-del-${it.id}`}>
                      <Trash2 className="w-4 h-4 text-[#BC4749]" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
